import type React from 'react';
import {Alert, Box, Skeleton, Stack} from '@mui/material';
import {generatePath, useLocation, useNavigate, useOutletContext} from 'react-router';
import {routes} from '~/routing/routes';
import {downloadCsv} from '~/utils/downloadCsv';
import {useCategoryRecordQuery} from '../../hooks/useCategoryRecord';
import {useProfileSpendQuery} from '../../hooks/useCategoryProfileQueries';
import {useRecordActions} from '../../hooks/useRecordActions';
import type {Breakdown} from '../../types/categoryProfileTypes';
import {BreakdownChart} from '../charts/BreakdownChart';
import {BudgetVsActual} from '../charts/BudgetVsActual';
import {DOCUMENT_MIX_SEGMENTS, DocumentMixChart} from '../charts/DocumentMixChart';
import {GeoSpendMap} from '~/components/visualization/geo-spend-map/GeoSpendMap';
import {SpendTrendChart} from '../charts/SpendTrendChart';
import {VolumeChart} from '../charts/VolumeChart';
import {ChartCard} from '../components/ChartCard';
import {ProfileSection} from '../components/ProfileSection';
import {CostIntelligenceSection} from '../overview/CostIntelligenceSection';
import {PriceMonitorSection} from '../overview/PriceMonitorSection';
import type {CategoryProfileOutletContext} from '../profileTypes';
import {NotesSection} from '../record/NotesSection';
import {GeneralInformationSection} from '../record/GeneralInformationSection';
import {CategoryRisksSection} from '../record/CategoryRisksSection';
import {RequirementsSection} from '../record/RequirementsSection';
import {SpecificationsSection} from '../record/SpecificationsSection';
import {TeamSection} from '../record/TeamSection';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

const GRID_SX = {display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2};

/**
 * Overview (spec 4.4), in the spec's order: general information, cost intelligence, business requirements,
 * specifications, team, spend analysis and notes, with the contract price monitor after cost intelligence.
 */
export const OverviewTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const navigate = useNavigate();
    const {search} = useLocation();
    const {data, isLoading, isError} = useProfileSpendQuery(databagId, path);
    const recordQuery = useCategoryRecordQuery(databagId, path);
    const record = recordQuery.data;
    const actions = useRecordActions(databagId, path, record);

    const openTab = (tab: string) => navigate({pathname: generatePath(routes.category_profile, {id: String(databagId), tab}), search});
    const breakdown = (title: string, key: string, rows: Breakdown, emptyText: string, subtitle?: string, help?: string) => (
        <ChartCard title={title} subtitle={subtitle} help={help}
                   onDownload={rows.length ? () => downloadCsv(`${title} - ${header.name}`, [key, 'Spend'],
                       rows.map(r => [r.key || '(not set)', r.spend])) : undefined}>
            <BreakdownChart data={rows} money={money} emptyText={emptyText}/>
        </ChartCard>
    );

    return (
        <Stack gap={3}>
            {recordQuery.isError && <Alert severity="error">Could not load the category record.</Alert>}
            {recordQuery.isLoading && <Skeleton variant="rounded" height={180}/>}
            {record && <GeneralInformationSection record={record} actions={actions} taxonomyDescription={header.description}/>}
            <CostIntelligenceSection databagId={databagId} path={path} openTab={openTab} money={money}/>
            <PriceMonitorSection databagId={databagId} path={path} openContracts={() => openTab('contracts')}/>
            {record && (
                <>
                    <CategoryRisksSection record={record} actions={actions} flags={header.flags}/>
                    <RequirementsSection databagId={databagId} record={record} actions={actions}/>
                    <SpecificationsSection categoryName={header.name}/>
                    <TeamSection record={record} actions={actions}/>
                </>
            )}

            <ProfileSection title="Spend analysis" help={OVERVIEW_HELP.spendAnalysis} bare>
                {isError && <Alert severity="error">Could not load the spend analysis.</Alert>}
                {isLoading && <Skeleton variant="rounded" height={320}/>}
                {data && (
                    <Box sx={GRID_SX}>
                        <ChartCard title="Spend trend" subtitle="Monthly, against the same month a year earlier" help={OVERVIEW_HELP.trend}
                                   onDownload={() => downloadCsv(`Spend trend - ${header.name}`,
                                       ['Month', 'Spend', 'Spend a year earlier', 'Spike'],
                                       data.trend.map(p => [p.month.slice(0, 7), p.spend, p.priorSpend, p.spike ? 'yes' : '']))}>
                            <SpendTrendChart trend={data.trend} money={money}/>
                        </ChartCard>
                        <ChartCard title="Geographic distribution" subtitle="Supplier country" help={OVERVIEW_HELP.geography}
                                   onDownload={data.byCountry.length ? () => downloadCsv(`Spend by country - ${header.name}`,
                                       ['Country', 'Spend'], data.byCountry.map(r => [r.key || '(not set)', r.spend])) : undefined}>
                            <GeoSpendMap data={data.byCountry} money={money}/>
                        </ChartCard>
                        {breakdown('Supplier distribution', 'Supplier', data.bySupplier, 'No supplier spend.', 'Share of category spend', OVERVIEW_HELP.suppliers)}
                        {breakdown('Spend by business unit', 'Business unit', data.byBusinessUnit, 'No business units in this dataset.', undefined, OVERVIEW_HELP.businessUnits)}
                        {breakdown('Spend by sub-category', 'Sub-category', data.bySubCategory, 'Leaf category: no sub-categories.', undefined, OVERVIEW_HELP.subCategories)}
                        {breakdown('Spend by site', 'Site', data.bySite, 'No sites in this dataset.', undefined, OVERVIEW_HELP.sites)}
                        {breakdown('Spend by cost centre', 'Cost centre', data.byCostCentre, 'No cost centres in this dataset.', undefined, OVERVIEW_HELP.costCentres)}
                        <ChartCard title="Budget vs actual" subtitle={`FY${data.yearToDate.year}, ${data.yearToDate.months} of 12 months`}
                                   help={OVERVIEW_HELP.budget}>
                            <BudgetVsActual ytd={data.yearToDate} budget={record?.budget ?? null} canEdit={Boolean(record?.canEdit)}
                                            money={money} onSetBudget={b => void actions.setBudget(b).catch(() => undefined)}/>
                        </ChartCard>
                        <ChartCard title="Volume" help={OVERVIEW_HELP.volume} subtitle={data.volume ? `Actual and forecast, ${data.volume.unit}` : 'Actual and forecast'}
                                   onDownload={data.volume ? () => downloadCsv(`Volume - ${header.name}`,
                                       ['Month', `Actual (${data.volume!.unit})`, `Forecast (${data.volume!.unit})`],
                                       data.volume!.points.map(p => [p.month.slice(0, 7), p.actual, p.forecast])) : undefined}>
                            <VolumeChart volume={data.volume}/>
                        </ChartCard>
                        <ChartCard title="Document mix" subtitle="Lines this period" help={OVERVIEW_HELP.documentMix}
                                   onDownload={() => downloadCsv(`Document mix - ${header.name}`, ['Document type', 'Lines'],
                                       DOCUMENT_MIX_SEGMENTS.map(s => [s.label, data.documentMix[s.key]]))}>
                            <DocumentMixChart mix={data.documentMix}/>
                        </ChartCard>
                    </Box>
                )}
            </ProfileSection>
            {record && <NotesSection record={record} actions={actions}/>}
        </Stack>
    );
};
